import { Plus, Trash2, X } from "lucide-react";
import { useState } from "react";

import {
  FLOW_FIELD_LABELS,
  FLOW_FORMAT_OPTIONS,
  FLOW_OPERATOR_LABELS,
  FLOW_OPERATORS_BY_FIELD,
  MULTI_VALUE_OPERATORS,
  type FlowCondition,
  type FlowConditionField,
  type FlowConditionLogic,
  type FlowConditionOperator,
} from "@/modules/flow";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";

interface MultiValueInputProps {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder: string;
}

function MultiValueInput({ values, onChange, placeholder }: MultiValueInputProps) {
  const [input, setInput] = useState("");

  const addValue = () => {
    const next = input.trim();
    if (next && !values.includes(next)) {
      onChange([...values, next]);
    }
    setInput("");
  };

  return (
    <div className="flex min-h-9 flex-1 flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-2 py-1 shadow-xs focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50">
      {values.map((value) => (
        <span key={value} className="inline-flex h-6 items-center gap-1 rounded bg-muted px-2 text-xs">
          {value}
          <button
            aria-label={`删除 ${value}`}
            className="text-muted-foreground hover:text-foreground"
            onClick={() => onChange(values.filter((item) => item !== value))}
            type="button"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      <input
        className="h-6 min-w-28 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        onBlur={addValue}
        onChange={(event) => setInput(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === ",") {
            event.preventDefault();
            addValue();
          }
        }}
        placeholder={values.length ? "继续添加" : placeholder}
        value={input}
      />
    </div>
  );
}

function FormatValues({ values, onChange }: { values: string[]; onChange: (values: string[]) => void }) {
  return (
    <div className="flex flex-1 flex-wrap gap-1.5 rounded-md border border-input p-1.5">
      {FLOW_FORMAT_OPTIONS.map((option) => {
        const selected = values.includes(option.value);
        return (
          <button
            key={option.value}
            className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
              selected
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:text-foreground"
            }`}
            onClick={() =>
              onChange(
                selected
                  ? values.filter((value) => value !== option.value)
                  : [...values, option.value],
              )
            }
            type="button"
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

interface ConditionEditorProps {
  conditions: FlowCondition[];
  logic: FlowConditionLogic;
  errors?: Record<number, string>;
  onChange: (conditions: FlowCondition[]) => void;
  onLogicChange: (logic: FlowConditionLogic) => void;
}

export function ConditionEditor({
  conditions,
  logic,
  errors,
  onChange,
  onLogicChange,
}: ConditionEditorProps) {
  const updateCondition = (index: number, condition: FlowCondition) => {
    onChange(conditions.map((item, itemIndex) => itemIndex === index ? condition : item));
  };

  return (
    <div className="space-y-3">
      {conditions.length > 1 ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>多条条件时</span>
          <Select value={logic} onValueChange={(value) => onLogicChange(value as FlowConditionLogic)}>
            <SelectTrigger className="w-40" size="sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">满足全部条件</SelectItem>
              <SelectItem value="any">满足任一条件</SelectItem>
            </SelectContent>
          </Select>
        </div>
      ) : null}

      <div className="space-y-1">
        <div className="space-y-2 rounded-lg bg-muted/30 p-2">
          {conditions.map((condition, index) => {
          const multiValue = MULTI_VALUE_OPERATORS.has(condition.operator);
          const formatField = condition.field === "format";
          return (
            <div key={index} data-flow-error={Boolean(errors?.[index])}>
              <div className="flex flex-col gap-2 md:flex-row md:items-start">
              <Select
                value={condition.field}
                onValueChange={(value) => {
                  const field = value as FlowConditionField;
                  const operator = FLOW_OPERATORS_BY_FIELD[field][0];
                  updateCondition(index, { field, operator, value: "" });
                }}
              >
                <SelectTrigger className="w-full md:w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(FLOW_FIELD_LABELS).map(([value, label]) => (
                    <SelectItem key={value} value={value}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={condition.operator}
                onValueChange={(value) => {
                  const operator = value as FlowConditionOperator;
                  updateCondition(
                    index,
                    MULTI_VALUE_OPERATORS.has(operator)
                      ? { field: condition.field, operator, values: [] }
                      : { field: condition.field, operator, value: "" },
                  );
                }}
              >
                <SelectTrigger className="w-full md:w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FLOW_OPERATORS_BY_FIELD[condition.field].map((operator) => (
                    <SelectItem key={operator} value={operator}>
                      {FLOW_OPERATOR_LABELS[operator]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {formatField && multiValue ? (
                <FormatValues
                  values={condition.values ?? []}
                  onChange={(values) => updateCondition(index, { ...condition, values })}
                />
              ) : formatField ? (
                <Select
                  value={condition.value || undefined}
                  onValueChange={(value) => updateCondition(index, { ...condition, value })}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="选择类型" />
                  </SelectTrigger>
                  <SelectContent>
                    {FLOW_FORMAT_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : multiValue ? (
                <MultiValueInput
                  values={condition.values ?? []}
                  onChange={(values) => updateCondition(index, { ...condition, values })}
                  placeholder="输入后按回车添加"
                />
              ) : (
                <Input
                  className="flex-1"
                  onChange={(event) => updateCondition(index, { ...condition, value: event.target.value })}
                  placeholder="请输入条件值"
                  value={condition.value ?? ""}
                />
              )}

              <Button
                aria-label="删除条件"
                disabled={conditions.length === 1}
                onClick={() => onChange(conditions.filter((_, itemIndex) => itemIndex !== index))}
                size="icon"
                type="button"
                variant="ghost"
              >
                <Trash2 className="size-4" />
              </Button>
              </div>
              {errors?.[index] ? (
                <p className="mt-2 text-xs text-destructive">{errors[index]}</p>
              ) : null}
            </div>
          );
          })}
        </div>

        <Button
          className="gap-1.5 px-2 font-normal text-muted-foreground/70 hover:bg-transparent hover:text-foreground"
          onClick={() => onChange([...conditions, { field: "url", operator: "contains", value: "" }])}
          size="sm"
          type="button"
          variant="ghost"
        >
          <Plus className="size-4" />
          添加条件
        </Button>
      </div>
    </div>
  );
}
